import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Rss, Save } from "lucide-react";
import api, { errMsg } from "@/lib/api";

const FIELDS = [
  ["meta_pixel_id", "Meta Pixel ID"],
  ["meta_capi_token", "Meta Conversions API access token"],
  ["meta_dataset_id", "Meta dataset ID"],
  ["google_ads_conversion_id", "Google Ads conversion ID (AW-…)"],
  ["google_ads_purchase_label", "Google Ads purchase conversion label"],
  ["google_ads_customer_id", "Google Ads customer ID (for enhanced conversions API)"],
  ["ga4_measurement_id", "GA4 measurement ID (G-…)"],
  ["ga4_api_secret", "GA4 Measurement Protocol API secret"],
  ["gmc_merchant_id", "Google Merchant Center ID"],
  ["gsc_verification", "Search Console verification token"],
  ["bing_uet_id", "Microsoft/Bing UET tag ID"],
  ["google_business_profile", "Google Business Profile URL (for store locator later)"],
];

export default function AdminIntegrations() {
  const [integ, setInteg] = useState({});
  const [headScript, setHeadScript] = useState("");
  const [bodyScript, setBodyScript] = useState("");
  const [aiCrawlers, setAiCrawlers] = useState("");
  const [feeds, setFeeds] = useState(null);
  const [events, setEvents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const s = await api.get("/admin/settings");
    setInteg(s.data.integrations || {});
    setHeadScript((s.data.custom_scripts || {}).head || "");
    setBodyScript((s.data.custom_scripts || {}).body || "");
    setAiCrawlers((s.data.ai_crawlers_allowed || []).join(", "));
    api.get("/admin/feeds/status").then((r) => setFeeds(r.data)).catch(() => {});
    api.get("/admin/events/log?limit=20").then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load().catch((e) => toast.error(errMsg(e))); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const s = await api.get("/admin/settings");
      const updated = {
        ...s.data,
        integrations: integ,
        custom_scripts: { head: headScript, body: bodyScript },
        ai_crawlers_allowed: aiCrawlers.split(",").map((x) => x.trim()).filter(Boolean),
      };
      await api.put("/admin/settings", updated);
      toast.success("Integrations saved");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="integrations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Tags & Integrations</h1>
          <p className="text-sm text-neutral-500">All IDs are entered here, never hardcoded. Server-side events fan out to Meta CAPI and GA4 automatically once credentials exist.</p>
        </div>
        <button onClick={save} disabled={busy} className="px-4 py-2 bg-brand text-white rounded text-xs font-medium flex items-center gap-1.5" data-testid="integrations-save">
          <Save size={13} /> Save all
        </button>
      </div>

      <section className="bg-white border rounded p-4 grid md:grid-cols-2 gap-3 text-xs" data-testid="integration-fields">
        {FIELDS.map(([key, label]) => (
          <label key={key} className="block">
            <span className="text-neutral-500">{label}</span>
            <input value={integ[key] || ""} onChange={(e) => setInteg({ ...integ, [key]: e.target.value })}
              type={/token|secret/i.test(label) ? "password" : "text"}
              className="border rounded px-2 py-1.5 w-full mt-1 font-mono" data-testid={`integ-${key}`} />
          </label>
        ))}
      </section>

      <section className="bg-white border rounded p-4 space-y-3 text-xs" data-testid="custom-scripts">
        <b className="text-sm">Custom scripts</b>
        <p className="text-neutral-500">Injected after consent. Head slot for verification tags, body slot for widgets.</p>
        <label className="block"><span className="text-neutral-500">&lt;head&gt; script</span>
          <textarea value={headScript} onChange={(e) => setHeadScript(e.target.value)} rows={3} className="border rounded p-2 w-full font-mono mt-1" data-testid="custom-head" /></label>
        <label className="block"><span className="text-neutral-500">&lt;body&gt; script</span>
          <textarea value={bodyScript} onChange={(e) => setBodyScript(e.target.value)} rows={3} className="border rounded p-2 w-full font-mono mt-1" data-testid="custom-body" /></label>
        <label className="block"><span className="text-neutral-500">AI crawlers allowed in robots.txt (comma-separated)</span>
          <input value={aiCrawlers} onChange={(e) => setAiCrawlers(e.target.value)} className="border rounded px-2 py-1.5 w-full mt-1 font-mono" data-testid="ai-crawlers" /></label>
      </section>

      {feeds && (
        <section className="bg-white border rounded p-4 text-xs space-y-2" data-testid="feed-status">
          <b className="text-sm flex items-center gap-1.5"><Rss size={14} /> Catalogue feeds — auto-regenerate on every accepted rate change</b>
          <p className="text-neutral-500">Last rate applied: <span className="font-mono">{feeds.generated_at || "—"}</span> · {feeds.product_variants} variant rows</p>
          {Object.entries(feeds.feeds).map(([k, url]) => (
            <div key={k} className="flex items-center justify-between gap-3 py-1 border-b border-neutral-100 last:border-0">
              <span className="font-medium">{k.replace(/_/g, " ")}</span>
              <code className="text-neutral-500 truncate">{url}</code>
            </div>
          ))}
          <p className="text-neutral-400">{feeds.note}</p>
        </section>
      )}

      <section className="bg-white border rounded p-4 text-xs" data-testid="event-log">
        <b className="text-sm">Server-side event log (latest 20)</b>
        <div className="mt-2 max-h-56 overflow-y-auto divide-y divide-neutral-100">
          {events.map((e) => (
            <div key={e.id} className="py-1.5 flex items-center justify-between gap-3">
              <span className="font-medium">{e.event}</span>
              <span className="font-mono text-neutral-400 truncate">{e.event_id}</span>
              <span className="text-neutral-500">{Object.entries(e.delivery || {}).map(([k, v]) => `${k}:${v}`).join(" · ")}</span>
            </div>
          ))}
          {!events.length && <div className="py-3 text-neutral-400">No server-side events yet — they land here after a purchase, lead or signup.</div>}
        </div>
      </section>
    </div>
  );
}
